/**
 * Collapsible Editor AI loading bar experiment eligibility check.
 *
 * `platform_editor_ai_collapse_loading_bar` controls whether an Editor AI loading bar
 * minimises into a compact pill once the user scrolls away from it. It is deliberately
 * independent of the M1 streaming cohort so the loading bar can be rolled out on its own.
 *
 * Callers must pick the right variant:
 *
 * - `isCollapseLoadingBarEnabled` logs an experiment exposure. Use it at action-driven
 *   decision points and after any prerequisite checks, so only users who can receive the
 *   treatment are enrolled. The prerequisites must never include the experiment value
 *   itself - gating exposure on it enrols the test cohort only and leaves the readout
 *   without a control arm.
 * - `isCollapseLoadingBarEnabledNoExposure` logs nothing. Use it on render paths, where the
 *   canonical exposure has already been logged by an action-driven call site.
 *
 * Both variants default to `false`, so an absent or uninitialised experiment value leaves the
 * loading bar non-collapsible rather than changing the experience unexpectedly.
 */
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { UNSAFE_expValNoExposure } from '@atlaskit/platform-feature-experiments/unsafe-exp-val-no-exposure';

export const COLLAPSE_LOADING_BAR_EXPERIMENT = 'platform_editor_ai_collapse_loading_bar';

/**
 * Exposure-logging variant. Evaluate after prerequisite checks - see file-level JSDoc.
 */
export const isCollapseLoadingBarEnabled = (): boolean =>
	isExperimentEnabled(COLLAPSE_LOADING_BAR_EXPERIMENT);

/**
 * Non-exposure-logging variant for render paths - see file-level JSDoc.
 */
export const isCollapseLoadingBarEnabledNoExposure = (): boolean =>
	UNSAFE_expValNoExposure(COLLAPSE_LOADING_BAR_EXPERIMENT, 'isEnabled', false);
